import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_RELATIVE_PATH, API_BASE_PATH } from "../Config/Config";

const handleForbiddenRedirect = (error) => {
  if (error.response?.status === 403) {
    if (!localStorage.getItem("userDetail")) {
      localStorage.setItem("redirectAfterLogin", window.location.href);
    }
    window.location.replace("/LoginPage");
    return true;
  }
  return false;
};

// Shared-report links: the credential comes from the route params, not from the
// logged-in session, so every call here passes its header explicitly. The
// request interceptor leaves an explicit Authorization header alone.

// Test Suite Result Query
const fetchPublicTestSuiteResult = async ({ tsid, tsrid, companyId, otp }) => {
  if (!tsid || !companyId) throw new Error("Missing required parameters");

  try {
    const response = await axios.get(
      `${API_BASE_PATH}/KeyLogging/getTestSuiteResultForEmail?companyid=${companyId}&idtest_suite=${tsid}&TSRId=${tsrid}&otp=${otp}`,
      { headers: { Authorization: "" } },
    );
    return response.data;
  } catch (error) {
    if (handleForbiddenRedirect(error)) return null;
    throw error;
  }
};

export const usePublicTestSuiteResultQuery = (tsid, tsrid, companyId, otp, options) => {
  return useQuery({
    queryKey: ["publicTestSuiteResult", tsid, companyId, otp],
    queryFn: () => fetchPublicTestSuiteResult({ tsid, tsrid, companyId, otp }),
    enabled: options?.enabled && !!tsid && !!companyId && !!otp,
    retry: true,
    cacheTime: 90000,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });
};

// Test Case Results From Test Suite Query
const fetchPublicTestCaseRecords = async ({ id, criteria, companyId, otp }) => {
  if (!id || !companyId) throw new Error("Missing required parameters");

  try {
    const response = await axios.get(
      `${API_BASE_PATH}/KeyLogging/getTestCaseResultFromTestSuiteResultForEmail?companyid=${companyId}&tsrid=${id}&criteria=${criteria}&otp=${otp}`,
      { headers: { Authorization: "" } },
    );
    return response.data;
  } catch (error) {
    if (handleForbiddenRedirect(error)) return null;
    throw error;
  }
};

export const usePublicTestCaseResultsFromTestSuiteQuery = (
  id,
  criteria,
  companyId,
  otp,
  options,
) => {
  return useQuery({
    queryKey: ["publicTestCaseResultsFromTS", id, companyId, otp],
    queryFn: () => fetchPublicTestCaseRecords({ id, criteria, companyId, otp }),
    enabled: options?.enabled && !!id && !!companyId && !!otp,
    retry: true,
    cacheTime: 90000,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });
};

// Error Codes Query
const fetchPublicErrorCodes = async ({ id, companyId, otp }) => {
  if (!id || !companyId) throw new Error("Missing required parameters");

  try {
    const response = await axios.get(
      `${API_BASE_PATH}/KeyLogging/getErrorCodesForTestSuiteForEmail?companyid=${companyId}&tsrid=${id}&otp=${otp}`,
      { headers: { Authorization: "" } },
    );
    return response.data;
  } catch (error) {
    if (handleForbiddenRedirect(error)) return null;
    throw error;
  }
};
export const usePublicErrorCodesQuery = (id, companyId, otp, options) => {
  return useQuery({
    queryKey: ["publicErrorCodes", id, companyId, otp],
    queryFn: () => fetchPublicErrorCodes({ id, companyId, otp }),
    enabled: options?.enabled && !!id && !!companyId && !!otp,
  });
};

// Status Pattern Query
const fetchPublicStatusPattern = async ({ testSuiteResultId, companyId, otp }) => {
  if (!testSuiteResultId || !companyId) throw new Error("Missing required parameters");

  try {
    const response = await axios.get(
      `${API_BASE_PATH}/KeyLogging/getStatusPatternCountsForTestSuiteResultForEmail?companyid=${companyId}&tsrid=${testSuiteResultId}&otp=${otp}`,
      { headers: { Authorization: "" } },
    );
    return response.data;
  } catch (error) {
    if (handleForbiddenRedirect(error)) return null;
    throw error;
  }
};

export const usePublicStatusPatternQuery = (testSuiteResultId, companyId, otp, options) => {
  return useQuery({
    queryKey: ["publicStatusPattern", testSuiteResultId, companyId, otp],
    queryFn: () => fetchPublicStatusPattern({ testSuiteResultId, companyId, otp }),
    enabled: options?.enabled && !!testSuiteResultId && !!companyId && !!otp,
  });
};

const fetchPublicTestSuites = async ({ productId, companyId, otp }) => {
  if (!productId || !companyId) throw new Error("Missing required parameters");

  try {
    const response = await axios.get(
      `${API_BASE_PATH}/KeyLogging/getTestSuitesForEmail?companyid=${companyId}&productid=${productId}&otp=${otp}`,
      { headers: { Authorization: "" } },
    );
    return response.data;
  } catch (error) {
    if (handleForbiddenRedirect(error)) return null;
    throw error;
  }
};

export const usePublicTestSuitesQuery = (productId, companyId, otp, options) => {
  return useQuery({
    queryKey: ["publicTestSuites", productId, companyId, otp],
    queryFn: () => fetchPublicTestSuites({ productId, companyId, otp }),
    enabled: options?.enabled && !!productId && !!companyId && !!otp,
    retry: true,
    cacheTime: 90000,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });
};

const fetchPublicProducts = async ({ companyId, otp }) => {
  if (!companyId) throw new Error("Missing required parameters");

  try {
    const response = await axios.get(
      `${API_BASE_PATH}/KeyLogging/searchProdDropDownForEmail?companyid=${companyId}&otp=${otp}`,
      { headers: { Authorization: "" } },
    );
    return response.data;
  } catch (error) {
    if (handleForbiddenRedirect(error)) return null;
    throw error;
  }
};

export const usePublicProductsQuery = (companyId, otp, options) => {
  return useQuery({
    queryKey: ["publicProducts", companyId, otp],
    queryFn: () => fetchPublicProducts({ companyId, otp }),
    enabled: options?.enabled && !!companyId && !!otp,
    retry: true,
    cacheTime: 90000,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });
};
